import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RotateCcw, Copy, ClipboardPaste, Bookmark, Wand2, X } from "lucide-react";
import StyleEditor from "@/components/StyleEditor";
import { resolveWordStyle, baseStyle, STYLE_FIELDS } from "@/lib/style";
import { drawFrame } from "@/lib/animator";
import { api } from "@/lib/api";

export default function WordStyleDialog({ open, onOpenChange, config, setConfig, wordId, wordText, isHighlighted, copied, setCopied }) {
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [applyId, setApplyId] = useState("");
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => { if (open) api.listBrandPresets().then(setPresets).catch(() => {}); }, [open]);

  const effective = useMemo(() => resolveWordStyle(config, wordId, isHighlighted), [config, wordId, isHighlighted]);
  const hasOverride = !!(config.wordStyles || {})[wordId];

  const patchOverride = (patch) => setConfig((c) => ({
    ...c, wordStyles: { ...(c.wordStyles || {}), [wordId]: { ...((c.wordStyles || {})[wordId] || {}), ...patch } },
  }));

  const resetInherited = () => {
    setConfig((c) => { const ws = { ...(c.wordStyles || {}) }; delete ws[wordId]; return { ...c, wordStyles: ws }; });
    toast.success("Reset to inherited style");
  };
  const copyStyle = () => { setCopied(effective); toast.success(`Copied style of "${wordText}"`); };
  const pasteStyle = () => {
    if (!copied) { toast.error("Copy a style first"); return; }
    const ov = {}; STYLE_FIELDS.forEach((k) => { if (copied[k] !== undefined) ov[k] = copied[k]; });
    setConfig((c) => ({ ...c, wordStyles: { ...(c.wordStyles || {}), [wordId]: ov } }));
    toast.success("Pasted style");
  };
  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Name the preset"); return; }
    const cfgPatch = {};
    STYLE_FIELDS.forEach((k) => (cfgPatch[k] = effective[k]));
    try { await api.createBrandPreset(presetName.trim(), cfgPatch); setPresetName(""); setPresets(await api.listBrandPresets()); toast.success("Saved as preset"); }
    catch { toast.error("Could not save preset"); }
  };
  const applyPreset = () => {
    const p = presets.find((x) => x.id === applyId);
    if (!p) { toast.error("Pick a preset"); return; }
    const canon = baseStyle(p.config, isHighlighted);
    const ov = {}; STYLE_FIELDS.forEach((k) => (ov[k] = canon[k]));
    setConfig((c) => ({ ...c, wordStyles: { ...(c.wordStyles || {}), [wordId]: ov } }));
    toast.success(`Applied "${p.name}" to "${wordText}"`);
  };

  // live preview of just this word
  useEffect(() => {
    if (!open) return;
    const seg = [{ id: "p", text: wordText, start: 0, end: 1.2, words: [{ word: wordText, word_id: wordId, start: 0.15, end: 1.0, confidence: 1 }] }];
    const previewCfg = { ...config, animationTarget: config.animation === "karaoke" ? "karaoke" : "word", highlightWordIds: isHighlighted ? [wordId] : (config.highlightWordIds || []) };
    let start = performance.now();
    const draw = () => {
      const cv = canvasRef.current;
      if (cv) { const t = ((performance.now() - start) / 1000) % 1.4; drawFrame(cv.getContext("2d"), t, cv.width, cv.height, seg, previewCfg); }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, config, wordId, wordText, isHighlighted]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-0 overflow-hidden border-white/10 bg-[#121212] p-0" data-testid="word-style-dialog">
        <DialogTitle className="sr-only">Style word</DialogTitle>
        <DialogDescription className="sr-only">Edit the style override for the selected word.</DialogDescription>
        <div className="grid max-h-[88vh] md:grid-cols-[1fr_300px]">
          <div className="overflow-y-auto p-5">
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2 font-display text-lg font-black">
                <Wand2 size={18} className="text-[#8B5CF6]" /> Styling: <span className="text-[#00F0FF]">{wordText}</span>
              </div>
              <span data-testid="word-style-inherit-badge" className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${hasOverride ? "bg-[#8B5CF6]/20 text-[#a78bfa]" : "bg-white/5 text-[#A1A1AA]"}`}>
                {hasOverride ? "Custom override" : "Inherited"}
              </span>
            </div>
            <p className="mb-4 text-xs text-[#A1A1AA]">Changes apply to this word only. Unset options inherit from the default style.</p>

            <div className="mb-4 flex flex-wrap gap-2">
              <button data-testid="word-style-reset" onClick={resetInherited} disabled={!hasOverride} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-40"><RotateCcw size={13} /> Reset</button>
              <button data-testid="word-style-copy" onClick={copyStyle} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><Copy size={13} /> Copy</button>
              <button data-testid="word-style-paste" onClick={pasteStyle} disabled={!copied} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-40"><ClipboardPaste size={13} /> Paste</button>
            </div>

            <StyleEditor value={effective} onPatch={patchOverride} showFont prefix="word-style" />

            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white"><Bookmark size={13} /> Presets</div>
              <div className="mb-2 flex items-center gap-2">
                <input data-testid="word-style-preset-name" value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Save this style as a preset…" className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
                <button data-testid="word-style-save-preset" onClick={savePreset} className="rounded-lg bg-white px-3 py-2 text-sm font-bold text-black hover:bg-white/90">Save</button>
              </div>
              <div className="flex items-center gap-2">
                <select data-testid="word-style-apply-select" value={applyId} onChange={(e) => setApplyId(e.target.value)} className="flex-1 rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 text-sm outline-none">
                  <option value="">Apply a preset to this word…</option>
                  {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button data-testid="word-style-apply-preset" onClick={applyPreset} className="rounded-lg bg-[#8B5CF6] px-3 py-2 text-sm font-bold text-white hover:bg-[#7c4ddb]">Apply</button>
              </div>
            </div>
          </div>

          <div className="flex flex-col border-l border-white/10 bg-[#0A0A0A] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Preview</span>
              <button onClick={() => onOpenChange(false)}><X size={16} className="text-[#A1A1AA] hover:text-white" /></button>
            </div>
            <div className="flex flex-1 items-center justify-center">
              <div className="checker overflow-hidden rounded-lg border border-white/10" style={{ aspectRatio: 9 / 16, height: "min(48vh, 420px)" }}>
                <canvas ref={canvasRef} width={360} height={640} className="h-full w-full" data-testid="word-style-preview" />
              </div>
            </div>
            <button data-testid="word-style-done" onClick={() => onOpenChange(false)} className="mt-4 w-full rounded-full bg-[#00F0FF] py-3 text-sm font-bold text-black hover:bg-[#00d4e0]">Done</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
